// User-scoped localStorage caches that live under GLOBAL keys and therefore
// must be wiped on logout, so the next account on this device never sees the
// previous user's data flash while a fresh fetch is in flight.
//
// Newer caches (djAutoSync, djImportSelection) key by userId and don't need
// this — prefer that pattern for anything new. Keys that describe the DEVICE
// rather than the account (theme, volume, DJ-app install paths like
// `serato.path`, hint dismissals) deliberately stay.
//
// The planner sets mirror (utils/plannerSetsCache.js) has its own clear
// function next to its read/write code; the logout thunk calls both.

// Web-only feed snapshot (read/written by redux/thunk/feedThunk.js, which
// imports the key from here). Its reader already rejects a snapshot whose
// userId doesn't match the current session, so this wipe is about not
// leaving the previous user's personalized feed at rest after logout.
export const WEB_FEED_CACHE_KEY = 'mg:feed:snapshot:v2';

const USER_SCOPED_KEYS = [
    WEB_FEED_CACHE_KEY,
    // DJ sidebar caches (desktop): the previous user's playlist names flashed
    // in the sidebar before the owner-scoped DB load. ("Last synced" is no
    // longer a localStorage key: it lives in the main process per account —
    // dj_sync_state, migration 017 — and reaches the slices through
    // DjSyncStateHydrator, which also refills them after this wipe.)
    'rekordbox.playlists',
    'serato.playlists',
    'traktor.playlists',
    'virtualdj.playlists',
    // Liked-track → local file path map (desktop, likesThunk.js).
    'mixgoat:likedLocalPaths',
    // DJ name pre-filled on set-share images (WhatsNextTab).
    'mixgoat-dj-name',
    // How far each track was listened to (utils/listenProgress.js — that
    // module also registers an in-memory reset; literal here to avoid a cycle).
    'mg:listen-progress:v1',
];

// IN-MEMORY user-scoped caches (module-level Maps/vars that outlive their
// component, e.g. stale-while-revalidate paint caches) register a reset here,
// right next to the cache they clear — no import cycles, and a module that
// isn't in the bundle registers nothing. Returns an unregister function.
const RESETS = new Set();
export const registerUserScopedReset = (fn) => {
    RESETS.add(fn);
    return () => RESETS.delete(fn);
};

export const clearUserScopedCaches = () => {
    for (const key of USER_SCOPED_KEYS) {
        try { localStorage.removeItem(key); } catch { /* private mode */ }
    }
    for (const fn of RESETS) {
        try { fn(); } catch { /* one bad reset must not skip the rest */ }
    }
};
